import { correctPeriod, deletePeriod, endEnrollment, enrollStudent, getStudent, startPause, updatePause } from '../academyApi';
import { addDays, isActiveOn, todayKST } from '../periodDates';

export const activePause = (detail) => detail.pauses.find((p) => isActiveOn(p, todayKST()));

/** 현재 재원에 속한 편성만 정리한다. 미래 반 이동은 뒤에서 취소해야 앞 편성 연결이 복원된다. */
export function pauseEnrollmentPlan(detail, startOn) {
  const tenure = detail.current.tenure;
  if (!tenure || !startOn) return [];
  const nextStart = detail.tenures.filter((t) => t.start_on > tenure.start_on).map((t) => t.start_on).sort()[0];
  return detail.enrollments.filter((e) => e.start_on >= tenure.start_on && (!nextStart || e.start_on < nextStart)
    && (!e.end_on || e.end_on > startOn))
    .map((e) => ({ ...e, operation: e.start_on >= startOn ? 'cancel' : e.end_on && !e.auto_closed ? 'correct' : 'end' }))
    .sort((a, b) => Number(b.operation === 'cancel') - Number(a.operation === 'cancel')
      || b.start_on.localeCompare(a.start_on) || b.id - a.id);
}

const changed = () => new Error('학생 정보가 바뀌었습니다. 새로 표시된 날짜와 반 목록을 확인한 뒤 다시 저장해 주세요.');

/** 매 저장 뒤 서버를 읽는다. 응답 유실·외부 수정 때는 멈추고 사용자가 최신 목록을 검토하게 한다. */
function workflowSession(detail, gat, { onSnapshot, onWrite = () => {}, onProgress = () => {} }) {
  let current = detail;
  const read = async (revision) => {
    current = await getStudent(detail.student_id, {}, gat);
    onSnapshot(current);
    if (revision != null && current.revision !== revision) throw changed();
    return current;
  };
  return {
    read,
    write: async (label, request) => {
      onProgress(`${label} 저장 중`);
      onWrite();
      const result = await request(current.revision);
      await read(result?.revision);
      onProgress(`${label} 완료`);
      return current;
    },
  };
}

export async function savePauseAndClasses({ detail, startOn, reasonCode, memo, ...callbacks }, gat) {
  const session = workflowSession(detail, gat, callbacks);
  let current = await session.read(detail.revision);
  const tenureId = current.current.tenure?.id;
  const allowedIds = new Set(current.enrollments.map((e) => e.id));
  let pause = activePause(current);
  if (pause && pause.start_on !== startOn) throw changed();
  if (!pause) {
    if (current.current.state !== 'enrolled') throw changed();
    current = await session.write('휴원', (ifRevision) => startPause(detail.student_id, { startOn, reasonCode, memo, ifRevision }, gat));
    pause = activePause(current);
  }
  if (!pause || pause.start_on !== startOn) throw changed();
  const pauseId = pause.id;
  // 취소로 앞 편성이 복원될 수 있으므로 매 단계 계획을 다시 만든다. 무한 재시도는 하지 않는다.
  for (let i = 0; i <= allowedIds.size * 2; i += 1) {
    if (current.current.tenure?.id !== tenureId || activePause(current)?.id !== pauseId) throw changed();
    const target = pauseEnrollmentPlan(current, startOn)[0];
    if (!target) return current;
    if (!allowedIds.has(target.id)) throw changed();
    const reason = `휴원 시작(${startOn})에 따른 반 편성 정리`;
    // eslint-disable-next-line no-await-in-loop -- 학생 revision을 순서대로 사용한다
    current = await session.write(`${target.class_name} ${target.operation === 'cancel' ? '입반 취소' : '퇴반'}`, (ifRevision) => {
      if (target.operation === 'cancel') return deletePeriod('enrollment', target.id, { reason, ifRevision }, gat);
      const lastDay = addDays(startOn, -1);
      if (target.operation === 'correct') return correctPeriod('enrollment', target.id, { last_day: lastDay }, { reason, ifRevision }, gat);
      return endEnrollment(target.id, { lastDay, memo: reason, ifRevision }, gat);
    });
  }
  throw new Error('반 편성이 계속 바뀌어 처리를 멈췄습니다. 남은 반 목록을 확인해 주세요.');
}

export async function saveResumeAndClasses({ detail, pauseId, endOn, classIds, ...callbacks }, gat) {
  let current = detail;
  // 이전 휴원 처리에서 남은 반이 있으면 먼저 정리한다. 입력 화면에도 이 대상을 표시한다.
  const open = activePause(current);
  if (open && open.id === pauseId && pauseEnrollmentPlan(current, open.start_on).length) {
    current = await savePauseAndClasses({ detail: current, startOn: open.start_on, ...callbacks }, gat);
  }
  const session = workflowSession(current, gat, callbacks);
  current = await session.read(current.revision);
  const pause = current.pauses.find((p) => p.id === pauseId);
  if (!pause || (pause.end_on && !pause.auto_closed && pause.end_on !== endOn)) throw changed();
  if (!pause.end_on || pause.auto_closed) {
    current = await session.write('복귀', (ifRevision) => updatePause(pauseId, { endOn, ifRevision }, gat));
  }
  if (current.current.state !== 'enrolled') throw changed();
  for (const classId of [...new Set(classIds)]) {
    if (current.current.state !== 'enrolled' || current.pauses.find((p) => p.id === pauseId)?.end_on !== endOn) throw changed();
    // 응답을 못 받았어도 해당 복귀일에 이미 편성된 반은 다시 넣지 않는다.
    if (current.enrollments.some((e) => e.class_id === Number(classId) && isActiveOn(e, endOn))) continue;
    // eslint-disable-next-line no-await-in-loop -- 직전 저장 revision으로 입반한다
    current = await session.write('복귀 반 입반', (ifRevision) => enrollStudent(detail.student_id, { classId: Number(classId), startOn: endOn, ifRevision }, gat));
  }
  return current;
}
